import axios from 'axios'

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || ''

      // Don't auto-logout for change-password endpoint (it's validation error, not auth error)
      if (requestUrl.includes('/auth/change-password')) {
        // Let the component handle the validation error
        return Promise.reject(error)
      }

      // Token expired or invalid - auto logout
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', { user: userData }),
  login: (credentials) => api.post('/auth/login', credentials),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  updateProfile: (profileData) => api.put('/auth/profile', { user: profileData }),
  deleteAccount: () => api.delete('/auth/account'),
  changePassword: (currentPassword, newPassword, passwordConfirmation) =>
    api.put('/auth/change-password', {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: passwordConfirmation
    }),
  // Password reset
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password, passwordConfirmation) => 
    api.post('/auth/reset-password', { token, password, password_confirmation: passwordConfirmation }),
  // Email verification
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: () => api.post('/auth/resend-verification'),
  // OAuth
  googleAuth: () => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1'
    window.location.href = `${baseUrl}/auth/google`
  },
  unlinkGoogle: () => api.delete('/auth/unlink-google'),
}

// Business API
export const businessAPI = {
  list: () => api.get('/businesses', {
    headers: { 'Cache-Control': 'no-cache' }
  }),
  get: (id) => api.get(`/businesses/${id}`, {
    headers: { 'Cache-Control': 'no-cache' }
  }),
  create: (businessData) => api.post('/businesses', { business: businessData }),
  update: (id, businessData) => api.put(`/businesses/${id}`, { business: businessData }),
  delete: (id) => api.delete(`/businesses/${id}`),
  scrapeReviews: (id) => api.post(`/businesses/${id}/scrape`),
}

// Widget API
export const widgetAPI = {
  list: () => api.get('/widgets'),
  get: (id) => api.get(`/widgets/${id}`),
  create: (widgetData) => api.post('/widgets', { widget: widgetData }),
  update: (id, widgetData) => api.put(`/widgets/${id}`, { widget: widgetData }),
  delete: (id) => api.delete(`/widgets/${id}`),
  preview: (id) => api.get(`/widgets/${id}/preview`),
  analytics: (id) => api.get(`/widgets/${id}/analytics`),
  templates: () => api.get('/widgets/templates'),
  generatePublicUrl: (id) => api.post(`/widgets/${id}/generate-public-url`),
  removePublicUrl: (id) => api.delete(`/widgets/${id}/remove-public-url`),
}

// Review API
export const reviewAPI = {
  list: (params) => api.get('/reviews', { params }),
  get: (id) => api.get(`/reviews/${id}`),
  toggleVisibility: (id) => api.patch(`/reviews/${id}/toggle-visibility`),
}

// Upload API
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },
}

// Public API (no auth required)
export const publicAPI = {
  widgetPreview: (id) => axios.get(`${API_BASE_URL}/widgets/${id}/preview`),
  widgetTemplates: () => axios.get(`${API_BASE_URL}/widgets/templates`),
  publicWidget: (publicUrl) => axios.get(`${import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:4000'}/public/${publicUrl}`),
}

export default api